import axios from 'axios';
import authService from './auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add authentication token to requests
apiClient.interceptors.request.use(
  async (config) => {
    const token = await authService.getIdToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle authentication errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - redirect to login
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Types
export interface BracketMatch {
  id: string;
  round: number;
  match_number: number;
  player1_id?: string;
  player2_id?: string;
  winner_id?: string;
  next_match_id?: string;
  status: string;
  scheduled_at?: string;
  completed_at?: string;
}

export interface Pool {
  id: string;
  name: string;
  player_ids: string[];
}

export interface Bracket {
  id: string;
  tournament_id: string;
  type: string;
  pools?: Pool[];
  matches: BracketMatch[];
  created_at: string;
  updated_at: string;
}

export interface DoublesConfig {
  allow_mens_doubles: boolean;
  allow_womens_doubles: boolean;
  allow_mixed_doubles: boolean;
}

export interface Tournament {
  id: string;
  name: string;
  description?: string;
  type: 'ladder' | 'round_robin_ladder';
  start_date: string;
  end_date: string;
  registration_start_date?: string;
  registration_end_date?: string;
  location: string;
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  allowed_genders?: ('male' | 'female' | 'other')[];
  min_level?: string;
  max_level?: string;
  registered_player_ids?: string[];
  bracket?: Bracket;
  third_set_format?: 'full_set' | 'match_tiebreak';
  min_players?: number;
  // Pool configuration for round robin tournaments
  num_pools?: number; // Optional: number of pools (if not set, auto-calculated)
  // New fields for singles/doubles support
  match_type_config?: 'singles' | 'doubles' | 'mixed_doubles';
  allowed_match_formats?: string[];
  doubles_config?: DoublesConfig;
  created_by?: string;
  manager_user_ids?: string[];
  created_at: string;
  updated_at: string;
}

export interface Player {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  ntrp_rating?: number;
  utr?: number;
  date_of_birth?: string;
  gender?: 'male' | 'female' | 'other';
  profile_image?: string;
  is_active: boolean;
  joined_at: string;
  last_played_at?: string;
}

export interface PlayerSummary {
  id: string;
  name: string;
  gender: 'male' | 'female';
}

export interface TeamSummary {
  id: string;
  team_name: string;
  players: PlayerSummary[];
}

export interface Team {
  id: string;
  tournament_id: string;
  team_name: string;
  player1: PlayerSummary;
  player2: PlayerSummary;
  doubles_type: 'men' | 'women' | 'mixed';
  created_at: string;
  updated_at: string;
}

export interface Match {
  id: string;
  tournament_id: string;
  tournament_name?: string;
  match_type: 'singles' | 'doubles';
  // For singles matches
  player1_id?: string;
  player2_id?: string;
  player1?: PlayerSummary;
  player2?: PlayerSummary;
  // For doubles matches
  team1_id?: string;
  team2_id?: string;
  team1?: TeamSummary;
  team2?: TeamSummary;
  // Legacy fields
  player1_team_id?: string;
  player2_team_id?: string;
  round: number;
  match_number: number;
  scheduled_time?: string;
  court?: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'walkover' | 'retired' | 'bye';
  score?: any;
  winner_player_id?: string;
  winner_team_id?: string;
  winner_id?: string; // Legacy
  completed_at?: string;
  pool_id?: string;
  pool_name?: string;
  next_match_id?: string;
  created_at: string;
  updated_at: string;
}

export interface DashboardStats {
  activeTournaments: number;
  upcomingMatches: number;
  activePlayers: number;
  matchesThisWeek: number;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'player' | 'tournament_manager' | 'admin';
  gender?: 'male' | 'female' | 'other';
  usta_level?: string;
  created_at: string;
  updated_at: string;
}

// API Functions
export const fetchDashboardStats = async (): Promise<DashboardStats> => {
  const response = await apiClient.get('/stats/dashboard');
  return response.data;
};

export const fetchTournaments = async (): Promise<Tournament[]> => {
  const response = await apiClient.get('/tournaments');
  return response.data;
};

// Fetch tournaments user is registered for
export const fetchMyTournaments = async (): Promise<Tournament[]> => {
  const response = await apiClient.get('/tournaments/my');
  return response.data;
};

// Fetch tournaments available for registration
export const fetchAvailableTournaments = async (): Promise<Tournament[]> => {
  const response = await apiClient.get('/tournaments/available');
  return response.data;
};

export const fetchTournament = async (id: string): Promise<Tournament> => {
  const response = await apiClient.get(`/tournaments/${id}`);
  return response.data;
};

export const createTournament = async (tournament: Partial<Tournament>): Promise<Tournament> => {
  const response = await apiClient.post('/tournaments', tournament);
  return response.data;
};

export const updateTournament = async (id: string, tournament: Partial<Tournament>): Promise<Tournament> => {
  const response = await apiClient.put(`/tournaments/${id}`, tournament);
  return response.data;
};

export const deleteTournament = async (id: string): Promise<void> => {
  await apiClient.delete(`/tournaments/${id}`);
};

export const fetchPlayers = async (): Promise<Player[]> => {
  const response = await apiClient.get('/players');
  return response.data;
};

export const fetchPlayer = async (id: string): Promise<Player> => {
  const response = await apiClient.get(`/players/${id}`);
  return response.data;
};

export const createPlayer = async (player: Partial<Player>): Promise<Player> => {
  const response = await apiClient.post('/players', player);
  return response.data;
};

export const fetchMatches = async (tournamentId?: string): Promise<Match[]> => {
  const url = tournamentId ? `/tournaments/${tournamentId}/matches` : '/matches';
  const response = await apiClient.get(url);
  return response.data;
};

export const fetchMatchById = async (matchId: string): Promise<Match> => {
  const response = await apiClient.get(`/matches/${matchId}`);
  return response.data;
};

export const createMatch = async (tournamentId: string, match: Partial<Match>): Promise<Match> => {
  const response = await apiClient.post(`/tournaments/${tournamentId}/matches`, match);
  return response.data;
};

export const updateMatchScore = async (matchId: string, score: any): Promise<Match> => {
  const response = await apiClient.put(`/matches/${matchId}/score`, { score });
  return response.data;
};

export const updateMatch = async (id: string, match: Partial<Match>): Promise<Match> => {
  const response = await apiClient.put(`/matches/${id}`, match);
  return response.data;
};

// User API Functions
export const fetchCurrentUser = async (): Promise<User> => {
  const response = await apiClient.get('/users/me');
  return response.data;
};

export const updateCurrentUser = async (user: Partial<User>): Promise<User> => {
  const response = await apiClient.put('/users/me', user);
  return response.data;
};

// Admin API Functions
export const fetchAllUsers = async (): Promise<User[]> => {
  const response = await apiClient.get('/admin/users');
  return response.data;
};

export const updateUserRole = async (userId: string, role: string): Promise<void> => {
  await apiClient.put(`/admin/users/${userId}/role`, { role });
};

export interface CreateUserData {
  email: string;
  name: string;
  gender?: string;
  usta_level?: string;
  role: string;
}

export interface CreateUserResponse {
  user: User;
  temporary_password: string;
}

export const createUser = async (userData: CreateUserData): Promise<CreateUserResponse> => {
  const response = await apiClient.post('/admin/users', userData);
  return response.data;
};

export const updateUser = async (userId: string, userData: { name?: string; gender?: string; usta_level?: string }): Promise<User> => {
  const response = await apiClient.put(`/admin/users/${userId}`, userData);
  return response.data;
};

export const resetUserPassword = async (userId: string): Promise<{ message: string; email: string; expires_in_minutes: number }> => {
  const response = await apiClient.post(`/admin/users/${userId}/reset-password`);
  return response.data;
};

export const deleteUser = async (userId: string): Promise<{ message: string }> => {
  const response = await apiClient.delete(`/admin/users/${userId}`);
  return response.data;
};

export const resetUserMFA = async (userId: string): Promise<{ message: string }> => {
  const response = await apiClient.post(`/admin/users/${userId}/reset-mfa`);
  return response.data;
};

// Request password reset (public endpoint - no auth required)
export const requestPasswordReset = async (username: string): Promise<{ message: string; expires_in_minutes: number }> => {
  const response = await axios.post(`${API_BASE_URL}/auth/request-password-reset`, {
    username,
  });
  return response.data;
};

// Complete password reset (public endpoint - no auth required)
export const completePasswordReset = async (token: string, newPassword: string): Promise<{ message: string }> => {
  const response = await axios.post(`${API_BASE_URL}/auth/reset-password`, {
    token,
    new_password: newPassword,
  });
  return response.data;
};

// MFA API Functions
export interface MFASetupResponse {
  secret_code: string;
  manual_entry_key: string;
}

export interface MFAVerifyResponse {
  success: boolean;
  backup_codes: string[];
  message: string;
}

export interface MFAStatusResponse {
  mfa_enabled: boolean;
  totp_configured: boolean;
  backup_codes_remaining: number;
  enabled_at?: string;
  last_used_at?: string;
}

// Helper function to get access token for MFA operations
const getAccessToken = async (): Promise<string> => {
  try {
    const sessionResult = await authService.getCurrentSession();
    if (!sessionResult.success || !sessionResult.session) {
      throw new Error('No active session. Please sign in again.');
    }
    
    const session = sessionResult.session;
    // Check the structure - Amplify returns tokens in different formats
    const accessToken = 
      session.tokens?.accessToken?.toString() || 
      (session.tokens?.accessToken as any)?.toString?.() ||
      (typeof session.tokens?.accessToken === 'string' ? session.tokens.accessToken : null);
    
    if (!accessToken) {
      console.error('Access token structure:', {
        tokens: session.tokens,
        accessToken: session.tokens?.accessToken,
        type: typeof session.tokens?.accessToken,
      });
      throw new Error('Access token not available. Please sign in again.');
    }
    return accessToken;
  } catch (error: any) {
    console.error('Error getting access token:', error);
    throw error;
  }
};

export const setupMFA = async (): Promise<MFASetupResponse> => {
  const accessToken = await getAccessToken();
  const response = await apiClient.post('/auth/mfa/setup', {}, {
    headers: {
      'X-Access-Token': accessToken,
    },
  });
  return response.data;
};

export const verifyMFA = async (code: string): Promise<MFAVerifyResponse> => {
  const accessToken = await getAccessToken();
  const response = await apiClient.post('/auth/mfa/verify', { code }, {
    headers: {
      'X-Access-Token': accessToken,
    },
  });
  return response.data;
};

export const disableMFA = async (code: string): Promise<{ success: boolean; message: string }> => {
  const accessToken = await getAccessToken();
  const response = await apiClient.post('/auth/mfa/disable', { code }, {
    headers: {
      'X-Access-Token': accessToken,
    },
  });
  return response.data;
};

export const getMFAStatus = async (): Promise<MFAStatusResponse> => {
  const response = await apiClient.get('/auth/mfa/status');
  return response.data;
};

export const useBackupCode = async (backupCode: string): Promise<{ success: boolean; codes_remaining: number }> => {
  const response = await apiClient.post('/auth/mfa/backup-code', { backup_code: backupCode });
  return response.data;
};

// Tournament Registration API Functions
export const getMyTournaments = async (): Promise<Tournament[]> => {
  const response = await apiClient.get('/tournaments/my');
  return response.data;
};

export const getAvailableTournaments = async (): Promise<Tournament[]> => {
  const response = await apiClient.get('/tournaments/available');
  return response.data;
};

export const registerForTournament = async (tournamentId: string): Promise<{ message: string }> => {
  const response = await apiClient.post(`/tournaments/${tournamentId}/register`);
  return response.data;
};

// Team API Functions
export interface CreateTeamData {
  player1_id: string;
  player2_id: string;
  doubles_type: 'men' | 'women' | 'mixed';
  team_name?: string;
}

export const fetchTeams = async (tournamentId: string, doublesType?: string): Promise<{ teams: Team[]; total: number }> => {
  const params = doublesType ? `?doubles_type=${doublesType}` : '';
  const response = await apiClient.get(`/tournaments/${tournamentId}/teams${params}`);
  return response.data;
};

export const fetchTeam = async (teamId: string): Promise<Team> => {
  const response = await apiClient.get(`/teams/${teamId}`);
  return response.data;
};

export const createTeam = async (tournamentId: string, teamData: CreateTeamData): Promise<Team> => {
  const response = await apiClient.post(`/tournaments/${tournamentId}/teams`, teamData);
  return response.data;
};

export const updateTeam = async (teamId: string, teamData: { team_name: string }): Promise<Team> => {
  const response = await apiClient.put(`/teams/${teamId}`, teamData);
  return response.data;
};

export const deleteTeam = async (teamId: string): Promise<void> => {
  await apiClient.delete(`/teams/${teamId}`);
};

export const unregisterFromTournament = async (tournamentId: string): Promise<{ message: string }> => {
  const response = await apiClient.delete(`/tournaments/${tournamentId}/register`);
  return response.data;
};

export const addPlayerToTournament = async (tournamentId: string, playerId: string): Promise<{ message: string }> => {
  const response = await apiClient.post(`/tournaments/${tournamentId}/players`, { player_id: playerId });
  return response.data;
};

export const addManagerToTournament = async (tournamentId: string, managerId: string): Promise<Tournament> => {
  const response = await apiClient.post(`/tournaments/${tournamentId}/managers`, { manager_id: managerId });
  return response.data;
};

export const removeManagerFromTournament = async (tournamentId: string, managerId: string): Promise<Tournament> => {
  const response = await apiClient.delete(`/tournaments/${tournamentId}/managers`, { data: { manager_id: managerId } });
  return response.data;
};

export interface EligibleUsersResponse {
  users: User[];
  registered_id_to_user_id_map?: Record<string, string>;
}

export const fetchEligibleUsers = async (tournamentId: string): Promise<User[]> => {
  try {
    const response = await apiClient.get<EligibleUsersResponse | User[]>(`/tournaments/${tournamentId}/eligible-users`);
    const data = response.data;
    
    // Handle both old format (array) and new format (object with users and mapping)
    if (Array.isArray(data)) {
      return data;
    }
    
    // New format: object with users array
    if (data && typeof data === 'object' && 'users' in data) {
      const users = (data as EligibleUsersResponse).users;
      return Array.isArray(users) ? users : [];
    }
    
    // Fallback: return empty array if format is unexpected
    console.warn('Unexpected response format from fetchEligibleUsers:', data);
    return [];
  } catch (error) {
    console.error('Error fetching eligible users:', error);
    return [];
  }
};

export const removePlayerFromTournament = async (tournamentId: string, playerId: string): Promise<{ message: string }> => {
  const response = await apiClient.delete(`/tournaments/${tournamentId}/players/${playerId}`);
  return response.data;
};

export const startTournament = async (tournamentId: string): Promise<Tournament> => {
  const response = await apiClient.post(`/tournaments/${tournamentId}/start`);
  return response.data;
};

export const endTournament = async (tournamentId: string): Promise<Tournament> => {
  const response = await apiClient.post(`/tournaments/${tournamentId}/end`);
  return response.data;
};

export const resetTournament = async (tournamentId: string): Promise<Tournament> => {
  const response = await apiClient.post(`/tournaments/${tournamentId}/reset`);
  return response.data;
};

export const populateEliminationBracket = async (tournamentId: string): Promise<{ message: string }> => {
  const response = await apiClient.post(`/tournaments/${tournamentId}/populate-bracket`);
  return response.data;
};

// Change password
export const changePassword = async (oldPassword: string, newPassword: string) => {
  const response = await apiClient.post('/users/me/change-password', {
    old_password: oldPassword,
    new_password: newPassword,
  });
  return response.data;
};

export default apiClient;
